import { logger } from '@/lib/logger';

const STORAGE_KEY_PREFIX = 'ahoy-bulk-action-active-job-';

const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getStorageKey(userId: number, orgId: number): string {
  return `${STORAGE_KEY_PREFIX}${userId}-${orgId}`;
}

interface PersistedBulkActionJob {
  jobId: number;
  totalCount: number;
  entityTypeName: string;
  createdAt: number;
}

export function getActiveBulkActionJob(
  userId: number | undefined,
  orgId: number | undefined
): PersistedBulkActionJob | null {
  if (userId === undefined || orgId === undefined) {
    return null;
  }

  try {
    const raw = localStorage.getItem(getStorageKey(userId, orgId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as Record<string, unknown>).jobId !== 'number' ||
      typeof (parsed as Record<string, unknown>).totalCount !== 'number' ||
      typeof (parsed as Record<string, unknown>).createdAt !== 'number'
    ) {
      return null;
    }
    const rawEntityType = (parsed as Record<string, unknown>).entityTypeName;
    // entityTypeName was added after the initial release; tolerate older
    // entries by falling back to '' (the banner re-derives labels from the
    // job response after the first poll).
    const entityTypeName =
      typeof rawEntityType === 'string' && rawEntityType.length > 0
        ? rawEntityType
        : '';
    const job: PersistedBulkActionJob = {
      jobId: (parsed as { jobId: number }).jobId,
      totalCount: (parsed as { totalCount: number }).totalCount,
      entityTypeName,
      createdAt: (parsed as { createdAt: number }).createdAt,
    };
    if (Date.now() - job.createdAt > TTL_MS) {
      return null;
    }
    return job;
  } catch (err) {
    logger.warn('Failed to read bulk action job from localStorage', {
      component: 'crm-bulk-action',
      error: String(err),
    });
    return null;
  }
}

export function setActiveBulkActionJob(
  userId: number,
  orgId: number,
  jobId: number,
  totalCount: number,
  entityTypeName: string
): void {
  try {
    localStorage.setItem(
      getStorageKey(userId, orgId),
      JSON.stringify({
        jobId,
        totalCount,
        entityTypeName,
        createdAt: Date.now(),
      })
    );
  } catch (err) {
    logger.warn('Failed to persist bulk action job to localStorage', {
      component: 'crm-bulk-action',
      error: String(err),
    });
  }
}

export function clearActiveBulkActionJob(userId: number, orgId: number): void {
  try {
    localStorage.removeItem(getStorageKey(userId, orgId));
  } catch (err) {
    logger.warn('Failed to clear bulk action job from localStorage', {
      component: 'crm-bulk-action',
      error: String(err),
    });
  }
}
